<template>
    <div class="project-details" v-if="project">
      <div class="header">
        <h2>{{ isEditMode ? "Edit Project" : project.name }}</h2>

        <div class="top-buttons">
          <button @click="showHierarchyMap = true" class="icon-btn" title="Show project hierarchy map">
            <i class="bi bi-diagram-3"></i>
          </button>
          <button v-if="['owner', 'admin'].includes(projectRole)" @click="showUserManagement = true" class="icon-btn">
            <i class="bi bi-people"></i>
          </button>
        <button @click="toggleEditMode" class="icon-btn">
          <i class="bi bi-pencil"></i>
        </button>
        <button class="remove-btn" @click="deleteProject">
          <i class="bi bi-trash"></i>
        </button>
        </div>
      </div>
  
      <!-- Display mode -->
      <div v-if="!isEditMode">
        <div class="detail"><strong>Project Number:</strong> {{ project.project_number }}</div>
        <div class="detail"><strong>Status:</strong> {{ project.status || '-' }}</div>
        <div class="detail"><strong>Start Date:</strong> {{ formatDate(project.start_date) }}</div>
        <div class="detail"><strong>End Date:</strong> {{ formatDate(project.end_date) }}</div>
        <div class="detail"><strong>Duration:</strong> {{ displayDuration }}</div>
      </div>
  
      <!-- Edit mode -->
      <div v-else>
        <div class="form-group">
          <label for="name">Name</label>
          <input type="text" id="name" v-model="editableProject.name">
        </div>
        <div class="form-group">
          <label for="project_number">Project Number</label>
          <input type="text" id="project_number" v-model="editableProject.project_number">
        </div>
        <div class="form-group">
          <label for="status">Status</label>
          <select id="status" v-model="editableProject.status">
            <option value="Sales Opportunity">Sales Opportunity</option>
            <option value="To be Started">To be Started</option>
            <option value="Ongoing">Ongoing</option>
            <option value="Finished">Finished</option>
          </select>
        </div>
        <div class="form-group">
          <label for="start_date">Start Date</label>
          <input type="datetime-local" id="start_date" v-model="editableProject.start_date">
        </div>
        <div class="form-group">
          <label for="end_date">End Date</label>
          <input type="datetime-local" id="end_date" v-model="editableProject.end_date">
        </div>
        <div class="buttons-container">
          <button @click="saveChanges">Save</button>
          <button @click="cancelEditMode">Cancel</button>
        </div>
      </div>
      <!-- User Management Blade -->
      <div v-if="showUserManagement" class="user-management-blade">
        <div class="blade-header">
          <h3>Project Members</h3>
          <button @click="showUserManagement = false" class="close-btn">X</button>
        </div>

        <table class="user-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Username</th>
              <th>Role</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="member in members" :key="member.id">
              <td>{{ member.name }}</td>
              <td>{{ member.username }}</td>
              <td @click="startEditingRole(member.id)">
                <div v-if="editingRoleUserId !== member.id">
                  {{ member.role }}
                </div>
                <select
                  v-else
                  v-model="member.role"
                  @change="updateMemberRole(member)"
                  @blur="editingRoleUserId = null"
                >
                  <option value="owner" :disabled="!canGrantOwnerRole()">Owner</option>
                  <option value="admin">Admin</option>
                  <option value="member">Member</option>
                  <option value="reader">Reader</option>
                </select>
              </td>
              <td>
                <button @click="removeMember(member.id)" class="remove-btn">Remove</button>
              </td>
            </tr>
          </tbody>
        </table>

        <div class="add-users-section">
        <div class="add-user-row">
          <multiselect
            v-model="selectedUsersToAdd"
            :options="filteredUsersToAdd"
            :custom-label="userLabel"
            label="name"
            track-by="id"
            placeholder="Search users to add"
            :multiple="true"
            :searchable="true"
            :close-on-select="false"
          />

          <select v-model="selectedRole">
            <option value="owner">Owner</option>
            <option value="admin">Admin</option>
            <option value="member">Member</option>
            <option value="reader">Reader</option>
          </select>

          <button @click="addUsersToProject">Add</button>
        </div>
      </div>
      </div>

      <ProjectHierarchyMap
        :open="showHierarchyMap"
        :project-id="project.id"
        :project-structure-set-id="project.structure_set_id"
        :project-name="project.name"
        @close="showHierarchyMap = false"
      />

      <div class="template-button-row">
        <button class="save-template-btn" @click="openSaveTemplateModal">Save as Template</button>
      </div>

      <div
        v-if="showSaveTemplateModal"
        class="template-modal-overlay"
        role="dialog"
        aria-modal="true"
        aria-label="Save project as template"
        @click.self="closeSaveTemplateModal"
      >
        <div class="template-modal">
          <div class="template-modal-header">
            <h3>Save as Template</h3>
            <button type="button" class="icon-btn" @click="closeSaveTemplateModal" :disabled="isSavingTemplate">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <div class="form-group">
            <label for="template_name">Template Name</label>
            <input type="text" id="template_name" v-model="templateForm.name" :disabled="isSavingTemplate">
          </div>

          <div class="form-group">
            <label for="template_description">Description (optional)</label>
            <input type="text" id="template_description" v-model="templateForm.description" :disabled="isSavingTemplate">
          </div>

          <div class="template-options">
            <label>
              <input type="checkbox" v-model="templateForm.include_structure_elements" @change="handleStructureElementsToggle" :disabled="isSavingTemplate">
              Include structure elements
            </label>
            <label>
              <input type="checkbox" v-model="templateForm.include_folders" @change="handleFoldersToggle" :disabled="isSavingTemplate">
              Include custom folders
            </label>

            <div v-if="templateForm.include_folders" class="folder-entity-options">
              <label>
                <input type="checkbox" v-model="templateForm.include_project_folders" :disabled="isSavingTemplate" @change="handleFolderEntityToggle">
                Project folders
              </label>
              <label>
                <input
                  type="checkbox"
                  v-model="templateForm.include_structure_element_folders"
                  :disabled="!templateForm.include_structure_elements || isSavingTemplate"
                  @change="handleFolderEntityToggle"
                >
                Structure element folders
              </label>
            </div>
          </div>

          <div class="template-modal-actions">
            <button type="button" @click="closeSaveTemplateModal" :disabled="isSavingTemplate">Cancel</button>
            <button type="button" @click="saveProjectAsTemplate" :disabled="isSavingTemplate || !canSubmitTemplate">
              {{ isSavingTemplate ? 'Saving...' : 'Save Template' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import "vue-multiselect/dist/vue-multiselect.css";
  import Multiselect from "vue-multiselect";
  import ProjectHierarchyMap from "@/components/ProjectHierarchyMap.vue";
  import { API_ENDPOINTS } from '@/services/config';
  import apiService from '@/services/apiService';
  import { confirmDelete } from "@/utils/confirmDelete";
  import { mapState } from 'vuex';
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  
  export default {
    components: { Multiselect, ProjectHierarchyMap },
    props: {
      project: {
        type: Object,
        required: true,
        default: () => ({}),
      },
    },
    data() {
      return {
        isEditMode: false,
        editableProject: {},
        projectRole: null,
        showUserManagement: false,
        members: [],
        allUsers: [],
        selectedUsersToAdd: [],
        selectedRole: 'member',
        editingRoleUserId: null,
        showHierarchyMap: false,
        showSaveTemplateModal: false,
        isSavingTemplate: false,
        templateForm: {
          name: '',
          description: '',
          include_structure_elements: true,
          include_folders: true,
          include_project_folders: true,
          include_structure_element_folders: true,
        },
      };
    },
    computed: {
      ...mapState(['loggedInUser']),
      selectedFolderEntities() {
        const entities = [];
        if (this.templateForm.include_project_folders) entities.push('project');
        if (this.templateForm.include_structure_element_folders) entities.push('structure_element');
        return entities;
      },
      canSubmitTemplate() {
        const hasValidFolderSelection = !this.templateForm.include_folders || this.selectedFolderEntities.length > 0;
        return this.templateForm.name.trim().length > 0 && hasValidFolderSelection;
      },
      displayDuration() {
        if (!this.project.start_date || !this.project.end_date) return 'N/A';
  
        const start = new Date(this.project.start_date);
        const end = new Date(this.project.end_date);
        const duration = end - start;
        const seconds = Math.floor((duration / 1000) % 60);
        const minutes = Math.floor((duration / 1000 / 60) % 60);
        const hours = Math.floor(duration / (1000 * 60 * 60));
  
        return `${hours} hour(s), ${minutes} minute(s), ${seconds} second(s)`;
      },
      filteredUsersToAdd() {
        const assignedUserIds = this.members.map(m => m.id);
        return this.allUsers.filter(user => !assignedUserIds.includes(user.id));
      }
    },
    setup() {
      const toast = useToast();
      return { toast };
    },
    async mounted() {
      await this.fetchProjectRole();
      await this.fetchProjectMembers();
      await this.fetchAllUsers();
    },
    methods: {
      formatDate(dateString) {
        if (!dateString) return 'N/A';
        const options = {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          timeZoneName: 'short'
        };
        return new Date(dateString).toLocaleDateString(undefined, options);
      },
      toggleEditMode() {
        this.isEditMode = true;
        this.editableProject = JSON.parse(JSON.stringify(this.project));
      },
      async saveChanges() {
        this.$emit('updateProject', this.editableProject);
        this.isEditMode = false;
      },
      cancelEditMode() {
        this.isEditMode = false;
      },
      async deleteProject() {
        const projectId = this.project.id;
        try {
          const isConfirmed = await confirmDelete("Are you sure you want to delete this Project?");
          if (!isConfirmed) return; // Exit if the user cancels

          await apiService.deleteResource(API_ENDPOINTS.project(projectId), this.loggedInUser.id, this.loggedInUser.entra_id);
          this.$router.push({ name: "Projects" });
        } catch (error) {
          console.error('Error deleting project:', error);
        }
      },
      async fetchProjectRole() {
        try {
          const response = await axios.get(API_ENDPOINTS.projectMemberships, {
            params: { 
              entra_id: this.loggedInUser.entra_id,
              user_id: this.loggedInUser.id,
              project_id: this.project.id
             }
          });
          this.projectRole = response.data.role;
        } catch (error) {
          console.error("Error fetching project role", error);
        }
      },
      async fetchProjectMembers() {
        try {
          const response = await axios.get(API_ENDPOINTS.projectMembership(this.project.id), {
          params: { entra_id: this.loggedInUser.entra_id }
        });
          this.members = response.data;
        } catch (error) {
          console.error("Error fetching members", error);
        }
      },
      async removeMember(userId) {
        if (this.isLastOwner(userId)) {
          this.toast.error("You cannot remove the last owner from the project.");
          return;
        }

        try {
          await axios.delete(API_ENDPOINTS.projectMemberships, {
            params: {
              entra_id: this.loggedInUser.entra_id,
              user_id: userId,
              project_id: this.project.id
            },
          });
          await this.fetchProjectMembers();
        } catch (error) {
          console.error("Failed to remove user", error);
        }
      },
      async fetchAllUsers() {
        try {
          const response = await apiService.get(API_ENDPOINTS.users);
          this.allUsers = response;
        } catch (error) {
          console.error("Error fetching users", error);
        }
      },
      async addUsersToProject() {
        try {
          if (this.selectedRole === 'owner' && !this.canGrantOwnerRole()) {
            alert("Only an owner can grant the 'owner' role.");
            return;
          }

          for (const user of this.selectedUsersToAdd) {
            const userMember = {
              user_id: user.id,
              project_id: this.project.id,
              role: this.selectedRole
            };
            await apiService.createResource(API_ENDPOINTS.projectMemberships, userMember, this.loggedInUser.entra_id);
          }

          this.selectedUsersToAdd = [];
          this.selectedRole = 'member';
          await this.fetchProjectMembers();
        } catch (error) {
          console.error("Error adding users to project", error);
        }
      },
      isLastOwner(userId) {
        const owners = this.members.filter(m => m.role === 'owner');
        return owners.length === 1 && owners[0].id === userId;
      },
      canGrantOwnerRole() {
        return this.projectRole === 'owner';
      },
      startEditingRole(userId) {
        this.editingRoleUserId = userId;
      },

      openSaveTemplateModal() {
        this.templateForm = {
          name: `${this.project.name} Template`,
          description: '',
          include_structure_elements: true,
          include_folders: true,
          include_project_folders: true,
          include_structure_element_folders: true,
        };
        this.showSaveTemplateModal = true;
      },

      closeSaveTemplateModal() {
        if (this.isSavingTemplate) return;
        this.showSaveTemplateModal = false;
      },

      handleStructureElementsToggle() {
        if (!this.templateForm.include_structure_elements) {
          this.templateForm.include_structure_element_folders = false;
          this.syncIncludeFoldersFromEntities();
        }
      },

      handleFoldersToggle() {
        if (this.templateForm.include_folders) {
          this.templateForm.include_project_folders = true;
          this.templateForm.include_structure_element_folders = this.templateForm.include_structure_elements;
          return;
        }

        this.templateForm.include_project_folders = false;
        this.templateForm.include_structure_element_folders = false;
      },

      handleFolderEntityToggle() {
        this.syncIncludeFoldersFromEntities();
      },

      syncIncludeFoldersFromEntities() {
        this.templateForm.include_folders = this.selectedFolderEntities.length > 0;
      },

      async saveProjectAsTemplate() {
        if (!this.canSubmitTemplate) {
          if (this.templateForm.include_folders && this.selectedFolderEntities.length === 0) {
            this.toast.error('Select at least one folder source when including custom folders.');
            return;
          }
          this.toast.error('Template name is required.');
          return;
        }

        this.isSavingTemplate = true;
        try {
          const payload = {
            entra_id: this.loggedInUser.entra_id,
            project_id: this.project.id,
            name: this.templateForm.name.trim(),
            description: this.templateForm.description?.trim() || null,
            include_structure_elements: this.templateForm.include_structure_elements,
            include_folders: this.templateForm.include_folders,
            include_project_folders: this.templateForm.include_folders ? this.templateForm.include_project_folders : false,
            include_structure_element_folders: this.templateForm.include_folders ? this.templateForm.include_structure_element_folders : false,
            folder_entities: this.templateForm.include_folders ? this.selectedFolderEntities : [],
          };

          const response = await apiService.createProjectTemplate(payload);
          this.toast.success(response?.message || 'Project template created successfully.');
          this.showSaveTemplateModal = false;
        } catch (error) {
          const message = error?.response?.data?.message || 'Failed to save project template.';
          this.toast.error(message);
          console.error('Failed to save project template', error);
        } finally {
          this.isSavingTemplate = false;
        }
      },

      async updateMemberRole(member) {
        if (member.role === 'owner' && !this.canGrantOwnerRole()) {
          alert("Only an owner can assign the 'owner' role.");
          return;
        }

        if (this.isLastOwner(member.id)) {
          this.toast.error("You cannot demote the last owner from the project.");
          return;
        }
        console.log("Member id: ", member.id)
        try {
          await apiService.updateResource(
            API_ENDPOINTS.projectMemberships,
            {
              changed_user_id: member.id,
              project_id: this.project.id,
              role: member.role,
            },
            this.loggedInUser.id,
            this.loggedInUser.entra_id
          );
          this.editingRoleUserId = null;
          await this.fetchProjectMembers();
        } catch (error) {
          console.error("Failed to update member role", error);
        }
      },
    },
  };
  </script>
  
  <style scoped lang="scss">
  @import "@/assets/variables.scss";
  
  .project-details {
    max-width: 800px;
    margin: 0 auto;
    padding: 20px;
    border-radius: 8px;
    background-color: $secondary-color;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow: visible;
  
    .buttons-container {
      display: flex;
      justify-content: space-between;
      width: 100%;
      align-items: flex-end;
  
      .left-buttons, .right-buttons {
        display: flex;
        gap: 10px;
      }
    }
    .top-buttons {
      display: flex;
      gap: 10px;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
  
      h2 {
        color: $primary-color;
        margin-bottom: 20px;
      }
    }
  
    .detail {
    margin-bottom: 3px;
      strong {
        color: black;
      }
    }

    .user-management-blade {
      width: 100%;
      margin-top: 20px;
      padding: 20px;
      background: #fff;
      border: 1px solid #ccc;
      border-radius: 8px;
      box-shadow: inset 0 0 5px rgba(0, 0, 0, 0.05);
      overflow: visible;
    }

    .blade-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .user-table {
      width: 100%;
      margin-top: 10px;
      border-collapse: collapse;

      th, td {
        padding: 8px;
        text-align: left;
      }

      td button.remove-btn {
        color: red;
        border: none;
        background: none;
        cursor: pointer;
      }
    }

    .add-user-row {
      display: flex;
      gap: 10px;
      align-items: center;
      position: relative;

      ::v-deep(.multiselect) {
        flex: 1;
      }

      ::v-deep(.multiselect__content-wrapper) {
        z-index: 10000 !important;
      }

      select {
        height: 38px;

      }

      button {
        white-space: nowrap;
        height: 38px;
      }
    }

    .add-users-section {
      display: flex;
      flex-direction: column;
      gap: 10px;
      margin-top: 20px;
      overflow: visible;

      select, button {
        padding: 5px;
      }
    }

    .template-button-row {
      width: 100%;
      display: flex;
      justify-content: flex-end;
      margin-top: 16px;

      .save-template-btn {
        padding: 8px 12px;
      }
    }

    .template-modal-overlay {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.35);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1200;
    }

    .template-modal {
      width: min(520px, calc(100vw - 32px));
      background: #fff;
      border-radius: 8px;
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .template-modal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;

      h3 {
        margin: 0;
        color: $primary-color;
      }
    }

    .template-options {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .folder-entity-options {
        margin-left: 20px;
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
    }

    .template-modal-actions {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      margin-top: 8px;
    }

  }
  </style>
  